import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Copy, Check, Loader2, X, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePixQr } from '@/lib/hooks/billing';
import { cn } from '@/lib/utils';

interface Props {
  paymentId: string | null;
  onClose: () => void;
}

export function PixPaymentModal({ paymentId, onClose }: Props) {
  const { data, isLoading, isError } = usePixQr(paymentId);
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!data?.payload) return;
    try {
      await navigator.clipboard.writeText(data.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <Dialog.Root open={!!paymentId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(94vw,440px)] rounded-3 border border-card-stroke bg-card shadow-clay',
            'data-[state=open]:animate-fade-in',
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-card-stroke px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-2 bg-mint-soft text-mint-deep">
                <QrCode className="h-4 w-4" strokeWidth={1.8} />
              </div>
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">
                  Pagar com PIX
                </span>
                <Dialog.Title className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg leading-[1.1] mt-0.5">
                  Aponte a câmera ou copie o código
                </Dialog.Title>
              </div>
            </div>
            <Dialog.Close
              aria-label="Fechar"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-2 text-fg-muted hover:text-fg hover:bg-card-soft transition-colors"
            >
              <X className="h-4 w-4" strokeWidth={1.8} />
            </Dialog.Close>
          </header>

          <div className="px-6 py-5">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-6 w-6 animate-spin text-fg-muted" strokeWidth={1.8} />
              </div>
            ) : isError || !data ? (
              <p className="text-sm text-warn text-center py-8">
                Não conseguimos buscar o QR code agora. Tente novamente em alguns segundos.
              </p>
            ) : (
              <>
                <div className="rounded-2 bg-white p-4 flex items-center justify-center mb-4">
                  <img
                    src={`data:image/png;base64,${data.encodedImage}`}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>

                <p className="text-xs text-fg-muted text-center mb-3">
                  O pagamento é confirmado automaticamente em segundos.
                </p>

                <div className="relative">
                  <textarea
                    readOnly
                    value={data.payload}
                    className="w-full h-20 text-[11px] font-mono p-3 pr-12 rounded-2 border border-card-stroke bg-card-soft text-fg-muted resize-none"
                  />
                  <button
                    type="button"
                    onClick={copy}
                    aria-label={copied ? 'Copiado' : 'Copiar código'}
                    className="absolute top-2 right-2 grid h-8 w-8 place-items-center rounded-2 text-fg-muted hover:text-mint-deep hover:bg-mint-soft transition-colors"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-mint-deep" strokeWidth={2} />
                    ) : (
                      <Copy className="h-4 w-4" strokeWidth={1.8} />
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-card-stroke px-6 py-3 bg-card-soft">
            <Dialog.Close asChild>
              <Button variant="ghost" type="button">Fechar</Button>
            </Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
